import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Settings, Trash2, ChevronRight, GitBranch } from 'lucide-react';
import EmptyState from '../../components/EmptyState';
import usePipelines from '../../hooks/usePipelines';
import PipelineEditor from '../../components/crm/PipelineEditor';

export default function AbleCRMPipelines() {
  const navigate = useNavigate();
  const {
    pipelines,
    loading,
    createPipeline,
    updatePipeline,
    deletePipeline,
    fetchPipeline,
    createStage,
    updateStage,
    deleteStage,
    reorderStages,
  } = usePipelines();

  const [showEditor, setShowEditor] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleCreate = () => {
    setEditingPipeline(null);
    setIsCreating(true);
    setShowEditor(true);
  };

  const handleEdit = async (pipeline) => {
    const detail = await fetchPipeline(pipeline.id);
    if (detail) {
      setEditingPipeline(detail);
      setIsCreating(false);
      setShowEditor(true);
    }
  };

  const handleSave = async (data) => {
    if (isCreating) {
      const result = await createPipeline({
        name: data.name,
        description: data.description,
        stages: data.stages.map((s) => ({
          name: s.name,
          color: s.color,
          position: s.position,
          is_win_stage: s.is_win_stage,
          is_loss_stage: s.is_loss_stage,
        })),
      });
      if (result) {
        setShowEditor(false);
      }
    } else if (editingPipeline) {
      await updatePipeline(editingPipeline.id, {
        name: data.name,
        description: data.description,
      });

      const existingIds = new Set(editingPipeline.stages.map((s) => s.id));
      const newStageIds = new Set();

      for (const stage of data.stages) {
        if (stage.id && existingIds.has(stage.id)) {
          newStageIds.add(stage.id);
          const original = editingPipeline.stages.find((s) => s.id === stage.id);
          if (
            original &&
            (original.name !== stage.name ||
              original.color !== stage.color ||
              original.is_win_stage !== stage.is_win_stage ||
              original.is_loss_stage !== stage.is_loss_stage)
          ) {
            await updateStage(editingPipeline.id, stage.id, {
              name: stage.name,
              color: stage.color,
              is_win_stage: stage.is_win_stage,
              is_loss_stage: stage.is_loss_stage,
            });
          }
        } else {
          await createStage(editingPipeline.id, {
            name: stage.name,
            color: stage.color,
            position: stage.position,
            is_win_stage: stage.is_win_stage,
            is_loss_stage: stage.is_loss_stage,
          });
        }
      }

      for (const stage of editingPipeline.stages) {
        if (!newStageIds.has(stage.id)) {
          await deleteStage(editingPipeline.id, stage.id);
        }
      }

      const reorderData = data.stages
        .filter((s) => s.id && !s.id.startsWith('new-'))
        .map((s) => ({ id: s.id, position: s.position }));
      if (reorderData.length > 0) {
        await reorderStages(editingPipeline.id, reorderData);
      }

      setShowEditor(false);
    }
  };

  const handleDelete = async (id) => {
    const success = await deletePipeline(id);
    if (success) {
      setDeleteConfirm(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-lime-50 dark:bg-lime-900/20 flex items-center justify-center">
            <GitBranch className="w-5 h-5 text-lime-600 dark:text-lime-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Pipelines
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage your sales pipelines and stages
            </p>
          </div>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-900 bg-lime-400 hover:bg-lime-500 rounded-xl transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Pipeline
        </button>
      </div>

      {pipelines.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">
          <EmptyState
            illustration="pipeline"
            headline="Create your first pipeline"
            description="Visualize your sales process with customizable stages. Track deals from lead to close."
            buttonLabel="Create Pipeline"
            onAction={handleCreate}
          />
        </div>
      ) : (
        <div className="space-y-3">
          {pipelines.map((pipeline) => (
            <div
              key={pipeline.id}
              className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-5 hover:border-gray-200 dark:hover:border-gray-600 transition-all hover:shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div
                  className="flex-1 cursor-pointer"
                  onClick={() => navigate(`/data/pipelines/${pipeline.id}`)}
                >
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {pipeline.name}
                    </h3>
                    {pipeline.is_default && (
                      <span className="px-2 py-0.5 text-xs bg-lime-50 text-lime-700 dark:bg-lime-900/20 dark:text-lime-400 rounded-full border border-lime-200 dark:border-lime-800">
                        Default
                      </span>
                    )}
                  </div>
                  {pipeline.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {pipeline.description}
                    </p>
                  )}
                  <div className="flex gap-4 mt-2.5 text-xs text-gray-500 dark:text-gray-400">
                    <span>{pipeline.stage_count} stages</span>
                    <span>{pipeline.deal_count} deals</span>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEdit(pipeline)}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                  {!pipeline.is_default && (
                    <button
                      onClick={() => setDeleteConfirm(pipeline.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/data/pipelines/${pipeline.id}`)}
                    className="p-2 text-gray-400 hover:text-lime-600 hover:bg-lime-50 dark:hover:bg-lime-900/20 rounded-lg"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="absolute inset-0 z-20" onClick={() => setDeleteConfirm(null)}></div>
          <div className="relative top-20 z-50 mx-auto p-6 border max-w-sm shadow-lg rounded-2xl bg-white dark:bg-gray-800 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Delete Pipeline?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              This action cannot be undone. Only pipelines with no deals can be deleted.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-xl"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditor && (
        <PipelineEditor
          pipeline={editingPipeline}
          isCreating={isCreating}
          onSave={handleSave}
          onClose={() => setShowEditor(false)}
        />
      )}
    </div>
  );
}
